import axios from 'axios';

class AuthService {
  private refreshPromise: Promise<string> | null = null;
  private readonly API_BASE_URL = 'http://localhost:3300/api/v1';

  /**
   * Refresh the authentication token
   */
  async refreshToken(): Promise<string> {
    // If there's already a refresh in progress, return that promise
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    const currentToken = localStorage.getItem('authToken');
    if (!currentToken) {
      throw new Error('No token available to refresh');
    }

    this.refreshPromise = this.performTokenRefresh(currentToken);

    try {
      const newToken = await this.refreshPromise;
      return newToken;
    } finally {
      // Clear the promise after completion (success or failure)
      this.refreshPromise = null;
    }
  }

  private async performTokenRefresh(currentToken: string): Promise<string> {
    try {
      const response = await axios.get(`${this.API_BASE_URL}/refresh-token`, {
        headers: {
          Authorization: `Bearer ${currentToken}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Token refresh response:', response);

      const responseData = response.data as { access_token?: string };
      const { access_token: newToken } = responseData;

      if (!newToken) {
        throw new Error('No token received from refresh endpoint');
      }

      // Update localStorage with new token
      localStorage.setItem('authToken', newToken);

      return newToken;
    } catch (error) {
      console.error('Token refresh failed:', error);

      // If refresh fails, clear all auth data but let components handle the redirect
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');

      throw error;
    }
  }

  /**
   * Check if token is expired or about to expire
   */
  isTokenExpired(token: string, bufferMinutes = 5): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expTime = payload.exp * 1000; // Convert to milliseconds
      const now = Date.now();
      const bufferTime = bufferMinutes * 60 * 1000; // Convert buffer to milliseconds

      return expTime - now < bufferTime;
    } catch (error) {
      console.error('Error parsing token:', error);
      return true; // Assume expired if we can't parse
    }
  }

  /**
   * Get current token, refreshing if necessary
   */
  async getValidToken(): Promise<string> {
    const currentToken = localStorage.getItem('authToken');

    if (!currentToken) {
      throw new Error('No authentication token available');
    }

    // Check if token is expired or about to expire
    if (this.isTokenExpired(currentToken)) {
      console.log('Token is expired or about to expire, refreshing...');
      return await this.refreshToken();
    }

    return currentToken;
  }

  /**
   * Clear all authentication data
   */
  clearAuthData(): void {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
  }
}

// Helper function to ensure valid token before critical operations
export const ensureValidToken = async (): Promise<void> => {
  try {
    await authService.getValidToken();
  } catch (error) {
    console.error('Failed to ensure valid token:', error);
    throw error;
  }
};

// Export singleton instance
export const authService = new AuthService();
export default authService;
